import React, { useState, useEffect } from "react";
import { Tabs, Row, Col, Spin, Card, Collapse, Button, message, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEnrollmentList,
  fetchDeletionList,
  fetchAdditionList,
  fetchInceptionList,
  fetchActiveList,
  fetchFullListForExport,
} from "../../Action/Actions";
// UPDATED: Removed context import
// import { useSelectedYear } from "../../Context/SelectYearContext";
import { getUserData } from "../login/LoginSlice";
// UPDATED: Added imports for Redux financial year state
import { selectFinancialYears, selectSelectedYearId } from "../../Redux/financialYearSlice";
import { UserOutlined, ExportOutlined, FileAddOutlined, FireOutlined, TeamOutlined } from "@ant-design/icons";
import DataTable from "../DataTable/DataTable";
import { saveAs } from "file-saver";

const RESTRICTED_POLICY_TYPE_IDS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];

const ListView = () => {
  const dispatch = useDispatch();
  const user = useSelector(getUserData);
  const AuthData = useSelector(getUserData);

  // UPDATED: Get financial year details from Redux instead of Context
  const selectedYearId = useSelector(selectSelectedYearId);
  const yearList = useSelector(selectFinancialYears);
  const selectedYear = yearList.find(year => year.id === selectedYearId)?.financial_year || "";

  const [activeTab, setActiveTab] = useState("");
  const [selectedPolicyNumber, setSelectedPolicyNumber] = useState(null);
  const [enrollmentData, setEnrollmentData] = useState({});
  const [deletionList, setDeletionList] = useState([]);
  const [additionList, setAdditionList] = useState([]);
  const [inceptionList, setInceptionList] = useState([]);
  const [activeList, setActiveList] = useState([]);
  const [selectedListType, setSelectedListType] = useState("active");
  const [loading, setLoading] = useState(false);
  const [selectedCard, setSelectedCard] = useState("active");
  const policyTypes = Object.keys(enrollmentData || {});
  const { Panel } = Collapse;
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
  });
  const [selectedPolicyid, setSelectedPolicyid] = useState(null);
  const [filters, setFilters] = useState({});

  // UPDATED: useEffect now depends on selectedYearId from Redux
  useEffect(() => {
    const fetchEnrollmentPolicyTypeData = async () => {
      // Guard clause prevents API call if IDs are not ready
      if (!AuthData?.corporate_id || !selectedYearId) {
        setLoading(false);
        setEnrollmentData({});
        setSelectedPolicyid(null);
        setSelectedPolicyNumber(null);
        return;
      }

      try {
        setLoading(true);
        // The thunk gets the financial year ID from the store internally
        const data = await dispatch(fetchEnrollmentList(AuthData.corporate_id));
        const policy_enrollment_details = data.policy_enrollment_details || {};

        // NEW: Filter out restricted policy types
        const filtered_policy_enrollment_details = {};
        for (const type in policy_enrollment_details) {
          // Robust Name-based check to skip restricted types
          const typeName = String(type).toLowerCase();
          if (typeName.includes('marine') || typeName.includes('fire')) {
            continue;
          }

          const policies = policy_enrollment_details[type] || [];
          const validPolicies = policies.filter(p => {
            const details = Array.isArray(p.policy_details) ? p.policy_details[0] : p.policy_details;
            const typeId = details?.policy_type_id || p.policy_type_id || p.policy_details?.policy_type_id;
            const isRestrictedId = RESTRICTED_POLICY_TYPE_IDS.includes(Number(typeId));
            return !isRestrictedId;
          });
          if (validPolicies.length > 0) {
            filtered_policy_enrollment_details[type] = validPolicies;
          }
        }

        setEnrollmentData(filtered_policy_enrollment_details);

        // Get policy types that actually have data (not empty arrays)
        const availableTabs = Object.keys(filtered_policy_enrollment_details || {}).filter(
          (tab) => filtered_policy_enrollment_details[tab] && filtered_policy_enrollment_details[tab].length > 0
        );

        // Use the first available type that has data
        const tabToUse = availableTabs.length > 0 ? availableTabs[0] : "";

        // Update activeTab to the first available type with data
        if (tabToUse && tabToUse !== activeTab) {
          setActiveTab(tabToUse);
        }

        if (policy_enrollment_details[tabToUse]?.length > 0) {
          const firstPolicy = policy_enrollment_details[tabToUse][0]?.policy_details;
          if (firstPolicy) {
            // Use the same logic as filter: policy_number if available, otherwise policy_id
            const policyNumber = firstPolicy.policy_number?.trim() || firstPolicy.policy_id;
            setSelectedPolicyNumber(policyNumber);
            setSelectedPolicyid(firstPolicy.policy_id);
            // Fetch the default list for the first policy, passing the fresh data and tab
            fetchActiveListData(firstPolicy.policy_id, 1, pagination.pageSize, policy_enrollment_details, tabToUse);
          }
        } else {
          // Reset if no policies found for the active tab
          setSelectedPolicyid(null);
          setSelectedPolicyNumber(null);
          setActiveList([]);
        }
      } catch (err) {
        console.error("Error fetching enrollment policy type data:", err);
        setEnrollmentData({}); // Clear data on error
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollmentPolicyTypeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, AuthData?.corporate_id, selectedYearId]);

  const getDisplayedData = () => {
    switch (selectedListType) {
      case "deletion": return deletionList || [];
      case "addition": return additionList || [];
      case "inception": return inceptionList || [];
      default: return activeList || [];
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);

    // Automatically select and fetch data for the first policy of the new tab
    const firstPolicyData = enrollmentData[key]?.[0];
    if (firstPolicyData?.policy_details) {
      const firstPolicy = firstPolicyData.policy_details;
      const policyNumber = firstPolicy.policy_number?.trim() || firstPolicy.policy_id;
      const policyId = firstPolicy.policy_id;
      setSelectedPolicyNumber(policyNumber);
      setSelectedPolicyid(policyId);
      // Fetch the default active list for the first policy, passing the current data and new tab key
      handleCardClick("active", policyId, enrollmentData, key);
    } else {
      // No policies in this tab, clear data
      setSelectedPolicyNumber(null);
      setSelectedPolicyid(null);
      setActiveList([]);
      setDeletionList([]);
      setAdditionList([]);
      setInceptionList([]);
      setPagination(prev => ({ ...prev, current: 1, total: 0 }));
    }
  };

  const handlePolicyNumberClick = (key) => {
    const selectedPolicy = enrollmentData[activeTab]?.find((p) => {
      const policyKey = p.policy_details?.policy_number?.trim() || p.policy_details?.policy_id;
      return policyKey === key;
    });
    if (selectedPolicy?.policy_details) {
      const policyId = selectedPolicy.policy_details.policy_id;
      const policyNumber = selectedPolicy.policy_details.policy_number?.trim() || selectedPolicy.policy_details.policy_id;
      setSelectedPolicyNumber(policyNumber);
      setSelectedPolicyid(policyId);
      handleCardClick("active", policyId, enrollmentData, activeTab); // Default to active list on policy change
    }
  };

  const handleTableChange = (pagination, tableFilters) => {
    const { current, pageSize } = pagination;
    
    // Extract search parameters from Ant Design filters
    const searchParams = Object.keys(tableFilters || {}).reduce((acc, key) => {
      if (tableFilters[key] && tableFilters[key].length > 0) {
        acc[key] = tableFilters[key][0];
      }
      return acc;
    }, {});
    
    setFilters(searchParams);

    switch (selectedListType) {
      case "deletion": fetchDeletionListData(selectedPolicyid, current, pageSize, enrollmentData, activeTab, searchParams); break;
      case "addition": fetchAdditionListData(selectedPolicyid, current, pageSize, enrollmentData, activeTab, searchParams); break;
      case "inception": fetchInceptionListData(selectedPolicyid, current, pageSize, enrollmentData, activeTab, searchParams); break;
      case "active": fetchActiveListData(selectedPolicyid, current, pageSize, enrollmentData, activeTab, searchParams); break;
      default: break;
    }
  };

  const fetchList = async (fetcher, setter, listType, policyid, page = 1, pageSize = pagination.pageSize, currentEnrollmentData = enrollmentData, currentActiveTab = activeTab, searchParams = filters) => {
    try {
      setLoading(true);
      const response = await dispatch(fetcher(policyid, page, pageSize, searchParams));

      const listContainer = response[`${listType}_list`] || response[`${listType}_data`] || response;
      const actualData = Array.isArray(listContainer) ? listContainer : (listContainer.data || []);

      // Use the provided enrollmentData and activeTab to find total if not in response
      const currentPolicy = currentEnrollmentData[currentActiveTab]?.find(p => p.policy_details?.policy_id === policyid);
      const counts = currentPolicy?.employee_count || {};
      const countKey = listType === 'active' ? 'active_list' :
        listType === 'addition' ? 'addition_list' :
          listType === 'deletion' ? 'deletion_list' :
            'inception_list';

      const totalFromEnrollment = counts[countKey] || 0;
      const actualTotal = listContainer.total || response.total || totalFromEnrollment || 0;
      const actualCurrentPage = listContainer.current_page || response.current_page || page;

      setter(actualData);
      setSelectedListType(listType);
      setPagination({
        current: actualCurrentPage,
        pageSize: pageSize,
        total: Number(actualTotal),
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50", "100"],
      });
    } catch (err) {
      console.error(`Error fetching ${listType} list:`, err);
    } finally {
      setLoading(false);
    }
  }

  const fetchDeletionListData = (policyid, page = 1, pageSize = pagination.pageSize, data = enrollmentData, tab = activeTab, searchParams = filters) => fetchList(fetchDeletionList, setDeletionList, 'deletion', policyid, page, pageSize, data, tab, searchParams);
  const fetchAdditionListData = (policyid, page = 1, pageSize = pagination.pageSize, data = enrollmentData, tab = activeTab, searchParams = filters) => fetchList(fetchAdditionList, setAdditionList, 'addition', policyid, page, pageSize, data, tab, searchParams);
  const fetchInceptionListData = (policyid, page = 1, pageSize = pagination.pageSize, data = enrollmentData, tab = activeTab, searchParams = filters) => fetchList(fetchInceptionList, setInceptionList, 'inception', policyid, page, pageSize, data, tab, searchParams);
  const fetchActiveListData = (policyid, page = 1, pageSize = pagination.pageSize, data = enrollmentData, tab = activeTab, searchParams = filters) => fetchList(fetchActiveList, setActiveList, 'active', policyid, page, pageSize, data, tab, searchParams);

  const handleCardClick = (cardType, policyIdToFetch = selectedPolicyid, data = enrollmentData, tab = activeTab) => {
    if (!policyIdToFetch) return;
    setSelectedCard(cardType);
    setFilters({}); // Reset filters when changing card type
    switch (cardType) {
      case "deletion": fetchDeletionListData(policyIdToFetch, 1, pagination.pageSize, data, tab, {}); break;
      case "addition": fetchAdditionListData(policyIdToFetch, 1, pagination.pageSize, data, tab, {}); break;
      case "inception": fetchInceptionListData(policyIdToFetch, 1, pagination.pageSize, data, tab, {}); break;
      case "active": fetchActiveListData(policyIdToFetch, 1, pagination.pageSize, data, tab, {}); break;
      default: break;
    }
  };

  const getTabIcon = (type) => {
    switch (type) {
      case "GMC": return <TeamOutlined style={{ marginRight: 8 }} />;
      case "GTL": return <UserOutlined style={{ marginRight: 8 }} />;
      case "GPA": return <TeamOutlined style={{ marginRight: 8 }} />;
      case "Parent policy": return <TeamOutlined style={{ marginRight: 8 }} />;
      case "Topup": return <FileAddOutlined style={{ marginRight: 8 }} />;
      case "Fire": return <FireOutlined style={{ marginRight: 8 }} />;
      default: return null;
    }
  };

  const handleExport = async () => {
    if (!selectedPolicyid) {
      message.error("Please select a policy to export.", 3);
      return;
    }

    message.loading({ content: `Preparing ${selectedListType} list for download...`, key: 'exporting' });
    setLoading(true);

    try {
      const downloadLink = await dispatch(fetchFullListForExport(selectedPolicyid, selectedListType));
      if (downloadLink && typeof downloadLink === 'string') {
        const fileName = `${selectedListType}_list_${selectedPolicyNumber || selectedPolicyid}_${selectedYear}.xlsx`;
        saveAs(downloadLink, fileName);
        message.success({ content: 'Your download has started!', key: 'exporting', duration: 3 });
      } else {
        throw new Error("A valid download link was not received from the server.");
      }
    } catch (error) {
      console.error("Failed to export data:", error);
      const errorMessage = typeof error === 'string' ? error : (error.message || "The export could not be completed.");
      message.error({ content: `Export failed: ${errorMessage}`, key: 'exporting', duration: 4 });
    } finally {
      setLoading(false);
    }
  };

  const PolicyDetailsTable = ({ policy }) => {
    const columns = [
      {
        title: "SI NO",
        dataIndex: "si_no",
        key: "si_no",
        render: (_, __, index) => {
          const pageIndex = pagination?.current || 1;
          const pageSize = pagination?.pageSize || 10;
          return (pageIndex - 1) * pageSize + index + 1;
        },
      },
      { title: "EMPLOYEE NAME", dataIndex: "employee_name", key: "employee_name", searchable: true },
      { title: "EMPLOYEE ID", dataIndex: "employee_code", key: "employee_code", searchable: true },
      { title: "MEMBER ID", dataIndex: "member_id", key: "member_id", searchable: true },
      { title: "AGE", dataIndex: "age", key: "age" },
      { title: "DOB", dataIndex: "dob", key: "dob" },
      { title: "GENDER", dataIndex: "gender", key: "gender", searchable: true },
      { title: "RELATION", dataIndex: "relationship", key: "relationship" },
      { title: "DATE OF JOINING", dataIndex: "doj", key: "doj" },
      { title: "ENDORSEMENT NO", dataIndex: "endorsement_number", key: "endorsement_number" },
      { title: "ENDORSEMENT DATE", dataIndex: "endorsement_date", key: "endorsement_date" },
      { title: "SUM INSURED", dataIndex: "sum_insured", key: "sum_insured", searchable: true },
      { title: "POLICY NUMBER", dataIndex: "policy_number", key: "policy_number" },
      { title: "EMPLOYEE MOBILE", dataIndex: "mobile_number", key: "mobile_number", searchable: true },
      { title: "EMPLOYEE EMAIL", dataIndex: "email", key: "email", searchable: true },
    ];

    return (
      <Card style={{ width: "100%" }}>
        <Card>
          <Collapse defaultActiveKey={["1"]} className="custom-collapse">
            <Panel header="Policy Details" key="1">
              <Row gutter={[16, 16]}>
                <Col className="col-lg-4 col-md-4 collapse-content"><strong>Policy Number:</strong> {policy.policy_number || "N/A"}</Col>
                <Col className="col-lg-4 col-md-4 collapse-content"><strong>Policy Start Date:</strong> {policy.policy_startdate || "N/A"}</Col>
                <Col className="col-lg-4 col-md-4 collapse-content"><strong>Policy End Date:</strong> {policy.policy_end_date || "N/A"}</Col>
                <Col className="col-lg-4 col-md-4 collapse-content"><strong>Insurer:</strong> {policy.policy_select_insurer || "N/A"}</Col>
                <Col className="col-lg-4 col-md-4 collapse-content"><strong>TPA:</strong> {policy.policy_select_tpa || "N/A"}</Col>
              </Row>
            </Panel>
          </Collapse>
          <div className="row justify-content-center px-3 py-3 bg-label">
            {(() => {
              const currentPolicyData = enrollmentData[activeTab]?.find(p => p.policy_details?.policy_id === selectedPolicyid);
              const counts = currentPolicyData?.employee_count || {};

              return (
                <>
                  <div className="col-lg-3 col-xl-3 col-md-6 col-sm-12">
                    <Card className="label-card" onClick={() => handleCardClick("active")} style={{ background: selectedCard === "active" ? "#c0e4eb" : "white", }}>
                      <div className="d-flex gap-2"><div className="my-auto label-icon-bg"><UserOutlined className="label-icon" /></div>
                        <div className="label-sec"><h6>Active List</h6><ul style={{ paddingLeft: "0px" }}><li style={{ display: "flex", justifyContent: "space-between", }}><span>Total </span> <span style={{ marginLeft: "8px", marginRight: "8px" }}>-</span><span>{counts.active_list || "0"}</span></li></ul></div>
                      </div>
                    </Card>
                  </div>
                  <div className="col-lg-3 col-xl-3 col-md-6 col-sm-12">
                    <Card className="label-card" onClick={() => handleCardClick("addition")} style={{ background: selectedCard === "addition" ? "#c0e4eb" : "white", }}>
                      <div className="d-flex gap-2"><div className="my-auto label-icon-bg"><UserOutlined className="label-icon" /></div>
                        <div className="label-sec"><h6>Addition List</h6><ul style={{ paddingLeft: "0px" }}><li style={{ display: "flex", justifyContent: "space-between", }}><span>Total </span> <span style={{ marginLeft: "8px", marginRight: "8px" }}>-</span><span>{counts.addition_list || "0"}</span></li></ul></div>
                      </div>
                    </Card>
                  </div>
                  <div className="col-lg-3 col-xl-3 col-md-6 col-sm-12">
                    <Card className="label-card" onClick={() => handleCardClick("inception")} style={{ background: selectedCard === "inception" ? "#c0e4eb" : "white", }}>
                      <div className="d-flex gap-2"><div className="my-auto label-icon-bg"><UserOutlined className="label-icon" /></div>
                        <div className="label-sec"><h6>Inception List</h6><ul style={{ paddingLeft: "0px" }}><li style={{ display: "flex", justifyContent: "space-between", }}><span>Total </span> <span style={{ marginLeft: "8px", marginRight: "8px" }}>-</span><span>{counts.inception_list || "0"}</span></li></ul></div>
                      </div>
                    </Card>
                  </div>
                  <div className="col-lg-3 col-xl-3 col-md-6 col-sm-12">
                    <Card className="label-card" onClick={() => handleCardClick("deletion")} style={{ background: selectedCard === "deletion" ? "#c0e4eb" : "white", }}>
                      <div className="d-flex gap-2"><div className="my-auto label-icon-bg"><UserOutlined className="label-icon" /></div>
                        <div className="label-sec"><h6>Deletion List</h6><ul style={{ paddingLeft: "0px" }}><li style={{ display: "flex", justifyContent: "space-between", }}><span>Total </span> <span style={{ marginLeft: "8px", marginRight: "8px" }}>-</span><span>{counts.deletion_list || "0"}</span></li></ul></div>
                      </div>
                    </Card>
                  </div>
                </>
              );
            })()}
          </div>
          <Row gutter={16} className="mb-3 justify-content-end align-items-center px-3">
            <Col><Button type="primary" icon={<ExportOutlined />} onClick={handleExport} className="d-flex"> Export </Button></Col>
          </Row>
          <DataTable columns={columns} dataSource={getDisplayedData()} loading={loading} rowKey="id" pagination={pagination} onChange={handleTableChange} />
        </Card>
      </Card>
    );
  };

  const tabItems = policyTypes?.map((type) => ({
    key: type,
    label: (<span style={{ display: "flex", alignItems: "center", gap: "5px" }}> {type} {getTabIcon(type)} </span>),
    children: (
      <>
        <Tabs
          activeKey={selectedPolicyNumber}
          onChange={handlePolicyNumberClick}
          // UPDATED: Removed fallback logic as API guarantees policy_number
          items={enrollmentData[type]?.map((policy) => {
            const key = policy.policy_details?.policy_number?.trim() || policy.policy_details?.policy_id;
            return {
              key: key,
              label: policy.policy_details?.policy_number?.trim() ? (
                policy.policy_details.policy_number.trim()
              ) : (
                <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>

                  <Tag
                    style={{
                      backgroundColor: "#FFFF00",
                      color: "#000000E0",
                      fontSize: "12px",
                      padding: "2px 8px",
                      margin: 0,
                      fontFamily: '"Plus Jakarta Sans", sans-serif',
                      borderRadius: "4px",
                    }}
                  >
                    Policy Number not issued
                  </Tag>
                </span>
              ),
            };
          })}
        />
        {selectedPolicyNumber && enrollmentData[type]?.some((p) => {
          const key = p.policy_details?.policy_number?.trim() || p.policy_details?.policy_id;
          return key === selectedPolicyNumber;
        }) ? (
          <Row gutter={[16, 16]} style={{ marginTop: "20px" }}>
            {enrollmentData[type]
              .filter((p) => {
                const key = p.policy_details?.policy_number?.trim() || p.policy_details?.policy_id;
                return key === selectedPolicyNumber;
              })
              .map((policy) => {
                const key = policy.policy_details?.policy_number?.trim() || policy.policy_details?.policy_id;
                return (<Col key={key} span={24}> <PolicyDetailsTable policy={policy.policy_details} /> </Col>);
              })}
          </Row>
        ) : (
          !loading && <div className="escalation-message">No List View Details Found.</div>
        )}
      </>
    ),
  }));

  return (
    <Spin spinning={loading}>
      <div className="escalation-matrix-container">
        <div className="escalation-content">
          <Row className="escalation-row-header">
            <span style={{ fontWeight: "700", fontSize: "20px" }}>
              {user?.corporate_name || "Guest"}
            </span>{" "}
            {/* UPDATED: This now correctly displays the financial year from Redux */}
            <span style={{ marginLeft: "8px" }}>
              (Financial Year: {selectedYear})
            </span>
          </Row>
        </div>
        {policyTypes.length > 0 ? (
          <Tabs activeKey={activeTab} onChange={handleTabChange} className="esc-tab" items={tabItems} />
        ) : (
          <Card style={{ marginTop: "20px" }}>
            <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
              <p style={{ fontSize: "16px", fontWeight: "500" }}>No enrollment data available for {selectedYear}</p>
            </div>
          </Card>
        )}
      </div>
    </Spin>
  );
}

export default ListView;